import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  LinearProgress,
  useTheme,
  alpha,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  Download as DownloadIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import * as indexedDBService from '../utils/indexedDBService';
import { SettingsService, SmtpSettings } from '../services/settingsService';
import { EmailService } from '../utils/emailService';
import { exportInvoiceToPdf } from '../utils/invoicePdfExporter';
import ZohoAkMoA4Template from '../components/templates/ZohoAkMoA4Template';

interface Invoice {
  id: string;
  invoiceNumber: string;
  client: string;
  clientId?: string;
  clientEmail?: string;
  clientDetails?: {
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    gstin?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  dueDate: string;
  amount: number;
  status: 'draft' | 'pending' | 'paid' | 'overdue' | 'deleted';
  paymentInfo?: any;
  items?: any[];
}

interface AgingBucket {
  label: string;
  color: string;
  bgColor: string;
  invoices: Invoice[];
  total: number;
}

const getAgingBucket = (dueDate: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Current';
  if (diffDays <= 30) return '1-30 Days';
  if (diffDays <= 60) return '31-60 Days';
  if (diffDays <= 90) return '61-90 Days';
  return '90+ Days';
};

const getDaysOverdue = (dueDate: string): number => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)));
};

const bucketConfig: Record<string, { color: string; bgColor: string; chipColor: string }> = {
  'Current':    { color: '#059669', bgColor: 'rgba(5,150,105,0.10)', chipColor: 'success' },
  '1-30 Days':  { color: '#D97706', bgColor: 'rgba(217,119,6,0.10)',  chipColor: 'warning' },
  '31-60 Days': { color: '#DC2626', bgColor: 'rgba(220,38,38,0.10)',  chipColor: 'error' },
  '61-90 Days': { color: '#7C3AED', bgColor: 'rgba(124,58,237,0.10)', chipColor: 'secondary' },
  '90+ Days':   { color: '#1F2937', bgColor: 'rgba(31,41,55,0.10)',   chipColor: 'default' },
};

const BUCKET_ORDER = ['Current', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days'];

const SummaryCard: React.FC<{
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}> = ({ title, value, subtitle, icon, color, bgColor }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 4,
      background: bgColor,
      border: `1px solid ${color}22`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'all 0.3s ease',
      '&:hover': { boxShadow: `0 8px 25px ${color}18`, transform: 'translateY(-4px)' },
    }}
  >
    <Box display="flex" alignItems="center" gap={2} mb={1.5}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '50%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: `${color}22`, color,
      }}>
        {icon}
      </Box>
      <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
        {title}
      </Typography>
    </Box>
    <Typography variant="h4" fontWeight="bold" sx={{ color }}>
      {value}
    </Typography>
    {subtitle && <Typography variant="caption" color="text.secondary">{subtitle}</Typography>}
  </Paper>
);

const Receivables: React.FC = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [bucketFilter, setBucketFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'info' });

  // Reminder dialog
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderInvoice, setReminderInvoice] = useState<Invoice | null>(null);
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderSubject, setReminderSubject] = useState('');
  const [reminderBody, setReminderBody] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderPdfBytes, setReminderPdfBytes] = useState<Uint8Array | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState<any[]>([]);
  const [businessSettings, setBusinessSettings] = useState<any>(null);
  const [smtpSettings, setSmtpSettings] = useState<SmtpSettings | null>(null);

  const fetchInvoices = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      const all = await indexedDBService.getAllItems<any>('invoices');
      // Fetch clients lookup map { id -> email }
      const allClients = await indexedDBService.getAllItems<any>('clients');
      const clientEmailMap: Record<string, string> = {};
      allClients.forEach((c: any) => {
        if (c.id && (c.email || c.contactEmail)) {
          clientEmailMap[c.id] = c.email || c.contactEmail || '';
        }
      });

      const unpaid = all
        .filter((inv: any) =>
          inv.userId === currentUser.uid &&
          (inv.status === 'pending' || inv.status === 'overdue') &&
          inv.status !== 'deleted'
        )
        .map((inv: any) => {
          const clientName = typeof inv.client === 'object'
            ? (inv.client?.name || 'Unknown')
            : (inv.client || inv.clientName || 'Unknown');
          // Resolve email: from invoice itself, or from clients store via clientId
          const clientEmail =
            inv.clientEmail ||
            inv.clientDetails?.email ||
            (inv.clientId ? clientEmailMap[inv.clientId] : '') ||
            '';
          return {
            ...inv,
            amount: Number(inv.amount) || 0,
            client: clientName,
            clientEmail,
          };
        });
      setInvoices(unpaid);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchEmailTemplates = useCallback(async () => {
    if (!currentUser) return;
    try {
      const settings = await SettingsService.fetchSettings(currentUser.uid);
      const s = settings as any;
      setEmailTemplates(s?.emailTemplates || []);
      // Extract SMTP settings (same approach as InvoicePreviewStep)
      if (s?.smtpSettings?.host) {
        setSmtpSettings(s.smtpSettings);
      }
      // Also grab business profile for PDF template
      const biz = await indexedDBService.getAllItems<any>('businesses');
      const myBiz = biz.find((b: any) => b.userId === currentUser.uid) || {};
      setBusinessSettings({ ...s, ...myBiz });
    } catch {}
  }, [currentUser]);

  useEffect(() => {
    fetchInvoices();
    fetchEmailTemplates();
  }, [fetchInvoices, fetchEmailTemplates]);

  // Pre-generate PDF attachment on opening the dialog to compute its exact size and speed up dispatch
  useEffect(() => {
    if (!reminderInvoice) {
      setReminderPdfBytes(null);
      return;
    }
    
    const generatePdfOnOpen = async () => {
      setGeneratingPdf(true);
      try {
        const { exportInvoiceToPdf } = await import('../utils/invoicePdfExporter');
        // Brief timeout to ensure the DOM node is rendered and ready at the root
        await new Promise(r => setTimeout(r, 400));
        const result = await exportInvoiceToPdf(
          'receivables-invoice-preview',
          `Invoice_${reminderInvoice.invoiceNumber}`,
          true,
          1.1 // Lightweight scale of 1.1 (reduces file size by 75%!)
        );
        if (result instanceof Uint8Array) {
          setReminderPdfBytes(result);
        }
      } catch (err) {
        console.error('Pre-generating PDF failed:', err);
      } finally {
        setGeneratingPdf(false);
      }
    };
    
    generatePdfOnOpen();
  }, [reminderInvoice]);

  const openReminderDialog = (invoice: Invoice) => {
    setReminderInvoice(invoice);
    setReminderEmail(invoice.clientEmail || '');

    // Try to find a "Payment Reminder" template
    const reminderTemplate = emailTemplates.find(t =>
      t.name?.toLowerCase().includes('reminder') || t.name?.toLowerCase().includes('payment')
    );

    const daysOverdue = getDaysOverdue(invoice.dueDate);
    const defaultBody = reminderTemplate
      ? reminderTemplate.body
          .replace(/{{customer_name}}/gi, invoice.client)
          .replace(/{{invoice_number}}/gi, invoice.invoiceNumber)
          .replace(/{{amount_due}}/gi, `₹${invoice.amount.toFixed(2)}`)
          .replace(/{{due_date}}/gi, invoice.dueDate)
      : `Dear ${invoice.client},\n\nThis is a friendly reminder that Invoice #${invoice.invoiceNumber} for ₹${invoice.amount.toFixed(2)} was due on ${invoice.dueDate} (${daysOverdue > 0 ? `${daysOverdue} days overdue` : 'today'}).\n\nPlease arrange payment at the earliest convenience.\n\nThank you,\nAccounts Team`;

    setReminderSubject(reminderTemplate?.subject?.replace(/{{invoice_number}}/gi, invoice.invoiceNumber) || `Payment Reminder – Invoice #${invoice.invoiceNumber}`);
    setReminderBody(defaultBody);
    setReminderOpen(true);
  };

  // Download the invoice PDF directly (saves file to disk)
  const downloadInvoicePdf = async () => {
    if (!reminderInvoice) return;
    try {
      await exportInvoiceToPdf(
        'receivables-invoice-preview',
        `Invoice_${reminderInvoice.invoiceNumber}`,
        false // trigger browser download
      );
      setSnackbar({ open: true, message: 'Invoice PDF downloaded!', severity: 'success' });
    } catch (err) {
      console.error('PDF download failed:', err);
      setSnackbar({ open: true, message: 'Failed to generate PDF', severity: 'error' });
    }
  };

  const sendReminder = async () => {
    if (!reminderEmail.trim()) {
      setSnackbar({ open: true, message: 'Please enter a valid email address', severity: 'error' });
      return;
    }
    setSendingReminder(true);
    try {
      // Use the pre-generated lightweight PDF bytes directly
      const pdfBytes = reminderPdfBytes || new Uint8Array(0);

      if (smtpSettings?.host) {
        // SMTP configured — send directly with attachment (same as InvoicePreviewStep)
        const res = await EmailService.sendInvoiceEmail(
          reminderEmail,
          reminderSubject,
          reminderBody.replace(/\n/g, '<br>'),
          pdfBytes,
          `Invoice_${reminderInvoice?.invoiceNumber}.pdf`,
          smtpSettings  // ← pass saved SMTP settings, exactly like InvoicePreviewStep
        );
        if (res && res.success && res.method !== 'client-fallback') {
          setSnackbar({ open: true, message: `Reminder sent with attachment to ${reminderEmail}`, severity: 'success' });
        } else {
          setSnackbar({ open: true, message: `Direct send failed. Opened email client as fallback.`, severity: 'error' });
        }
      } else {
        // No SMTP configured — download PDF + open mailto
        try {
          await exportInvoiceToPdf('receivables-invoice-preview', `Invoice_${reminderInvoice?.invoiceNumber}`, false);
        } catch {}
        const mailtoUrl = `mailto:${encodeURIComponent(reminderEmail)}?subject=${encodeURIComponent(reminderSubject)}&body=${encodeURIComponent(reminderBody)}`;
        window.open(mailtoUrl, '_blank');
        setSnackbar({ open: true, message: `Invoice PDF downloaded — attach it to the email that opened!`, severity: 'info' });
      }
      setReminderOpen(false);
    } catch (err) {
      console.error('Send reminder failed:', err);
      setSnackbar({ open: true, message: 'Failed to send reminder. Check SMTP settings.', severity: 'error' });
    } finally {
      setSendingReminder(false);
    }
  };

  // ── Aggregate ──────────────────────────────────────────────────────────────
  const totalOutstanding = invoices.reduce((s, inv) => s + inv.amount, 0);
  const overdueInvoices = invoices.filter(inv => getDaysOverdue(inv.dueDate) > 0);
  const totalOverdue = overdueInvoices.reduce((s, inv) => s + inv.amount, 0);
  const uniqueClients = new Set(invoices.map(inv => inv.client)).size;

  const agingMap: Record<string, AgingBucket> = {};
  BUCKET_ORDER.forEach(label => {
    agingMap[label] = {
      label,
      color: bucketConfig[label].color,
      bgColor: bucketConfig[label].bgColor,
      invoices: [],
      total: 0,
    };
  });
  invoices.forEach(inv => {
    const bucket = getAgingBucket(inv.dueDate);
    agingMap[bucket].invoices.push(inv);
    agingMap[bucket].total += inv.amount;
  });

  const filtered = invoices.filter(inv => {
    const matchSearch =
      !search ||
      inv.client.toLowerCase().includes(search.toLowerCase()) ||
      inv.invoiceNumber.toLowerCase().includes(search.toLowerCase());
    const matchBucket = bucketFilter === 'All' || getAgingBucket(inv.dueDate) === bucketFilter;
    return matchSearch && matchBucket;
  });

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Accounts Receivable
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Track outstanding invoices and send payment reminders
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchInvoices}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Total Outstanding"
            value={`₹${totalOutstanding.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
            subtitle={`${invoices.length} unpaid invoices`}
            icon={<MoneyIcon />}
            color="#2563EB"
            bgColor="rgba(37,99,235,0.07)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Overdue Amount"
            value={`₹${totalOverdue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`}
            subtitle={`${overdueInvoices.length} overdue invoices`}
            icon={<WarningIcon />}
            color="#DC2626"
            bgColor="rgba(220,38,38,0.07)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Clients with Dues"
            value={String(uniqueClients)}
            subtitle="unique clients"
            icon={<BusinessIcon />}
            color="#7C3AED"
            bgColor="rgba(124,58,237,0.07)"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            title="Avg Days Overdue"
            value={overdueInvoices.length > 0
              ? `${Math.round(overdueInvoices.reduce((s, inv) => s + getDaysOverdue(inv.dueDate), 0) / overdueInvoices.length)} days`
              : '0 days'}
            subtitle="across overdue invoices"
            icon={<AccessTimeIcon />}
            color="#D97706"
            bgColor="rgba(217,119,6,0.07)"
          />
        </Grid>
      </Grid>

      {/* Aging Summary Buckets */}
      <Typography variant="h6" fontWeight="bold" mb={1.5}>Aging Analysis</Typography>
      <Grid container spacing={2} mb={3}>
        {BUCKET_ORDER.map(bucket => {
          const data = agingMap[bucket];
          const cfg = bucketConfig[bucket];
          const pct = totalOutstanding > 0 ? (data.total / totalOutstanding) * 100 : 0;
          return (
            <Grid item xs={12} sm={6} md key={bucket}>
              <Paper
                elevation={0}
                onClick={() => setBucketFilter(bucketFilter === bucket ? 'All' : bucket)}
                sx={{
                  p: 2, borderRadius: 3, cursor: 'pointer',
                  border: `2px solid ${bucketFilter === bucket ? cfg.color : cfg.color + '33'}`,
                  background: cfg.bgColor,
                  transition: 'all 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 6px 20px ${cfg.color}22` },
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ color: cfg.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {bucket}
                </Typography>
                <Typography variant="h6" fontWeight="bold" mt={0.5}>
                  ₹{data.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
                <Typography variant="caption" color="text.secondary">{data.invoices.length} invoices</Typography>
                <LinearProgress
                  variant="determinate"
                  value={pct}
                  sx={{
                    mt: 1, borderRadius: 2, height: 5,
                    backgroundColor: `${cfg.color}22`,
                    '& .MuiLinearProgress-bar': { backgroundColor: cfg.color, borderRadius: 2 },
                  }}
                />
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Filters & Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box p={2} display="flex" gap={2} flexWrap="wrap" alignItems="center" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            placeholder="Search client or invoice #..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Aging Bucket</InputLabel>
            <Select
              value={bucketFilter}
              label="Aging Bucket"
              onChange={e => { setBucketFilter(e.target.value); setPage(0); }}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="All">All Buckets</MenuItem>
              {BUCKET_ORDER.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
            </Select>
          </FormControl>
          {(search || bucketFilter !== 'All') && (
            <Button size="small" onClick={() => { setSearch(''); setBucketFilter('All'); }}>
              Clear
            </Button>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)' }}>
                {['Client', 'Invoice #', 'Invoice Date', 'Due Date', 'Amount', 'Days Overdue', 'Aging Bucket', 'Actions'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(inv => {
                const daysOv = getDaysOverdue(inv.dueDate);
                const bucket = getAgingBucket(inv.dueDate);
                const cfg = bucketConfig[bucket];
                return (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        {inv.client}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.82rem' }}>{inv.invoiceNumber}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{inv.date}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: daysOv > 0 ? '#DC2626' : 'text.primary', fontWeight: daysOv > 0 ? 600 : 400 }}>
                      {inv.dueDate}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>₹{inv.amount.toFixed(2)}</TableCell>
                    <TableCell>
                      {daysOv > 0 ? (
                        <Chip label={`${daysOv}d`} size="small" sx={{ bgcolor: `${cfg.color}18`, color: cfg.color, fontWeight: 700, fontSize: '0.73rem' }} />
                      ) : (
                        <Chip label="Current" size="small" color="success" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={bucket}
                        size="small"
                        sx={{ bgcolor: cfg.bgColor, color: cfg.color, fontWeight: 600, fontSize: '0.73rem', border: `1px solid ${cfg.color}44` }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Send Payment Reminder">
                        <IconButton size="small" color="primary" onClick={() => openReminderDialog(inv)}>
                          <EmailIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                    <Typography variant="subtitle1" color="text.secondary">
                      {invoices.length === 0 ? 'All invoices are paid! 🎉' : 'No invoices match your filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      {/* Send Reminder Dialog */}
      <Dialog open={reminderOpen} onClose={() => setReminderOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon color="primary" /> Send Payment Reminder
        </DialogTitle>
        <DialogContent>
          {reminderInvoice && (
            <Box mb={2} p={1.5} borderRadius={2} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06), border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}` }}>
              <Typography variant="body2" color="text.secondary">Invoice</Typography>
              <Typography variant="subtitle1" fontWeight="bold">#{reminderInvoice.invoiceNumber} — {reminderInvoice.client}</Typography>
              <Typography variant="body2">Amount Due: <strong>₹{reminderInvoice.amount.toFixed(2)}</strong> &nbsp;|&nbsp; Due: <strong>{reminderInvoice.dueDate}</strong></Typography>
            </Box>
          )}
          <TextField
            fullWidth margin="dense" label="Recipient Email" value={reminderEmail}
            onChange={e => setReminderEmail(e.target.value)}
            placeholder="client@email.com"
            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            fullWidth margin="dense" label="Subject" value={reminderSubject}
            onChange={e => setReminderSubject(e.target.value)}
          />
          <TextField
            fullWidth margin="dense" label="Message" value={reminderBody}
            onChange={e => setReminderBody(e.target.value)}
            multiline rows={7}
          />

          <Box mt={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Attachments
            </Typography>
            <Box 
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 1, 
                p: 1.5, 
                display: 'flex', 
                alignItems: 'center',
                bgcolor: 'background.default'
              }}
            >
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  bgcolor: 'error.light',
                  color: 'error.main',
                  borderRadius: 1,
                  p: 1,
                  mr: 2
                }}
              >
                <PdfIcon />
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  Invoice_{reminderInvoice?.invoiceNumber}.pdf
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {generatingPdf 
                    ? 'PDF Document • Compressing & generating...' 
                    : `PDF Document • ${reminderPdfBytes ? (reminderPdfBytes.length / 1024).toFixed(1) : '0.0'} KB`}
                </Typography>
              </Box>
              <Chip 
                size="small" 
                icon={generatingPdf ? <CircularProgress size={12} /> : <AttachFileIcon fontSize="small" />} 
                label={generatingPdf ? 'Generating...' : 'Attached'} 
                color={generatingPdf ? 'default' : 'primary'} 
                variant="outlined" 
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          {/* Hint when no SMTP configured */}
          {!smtpSettings?.host && (
            <Box sx={{
              flex: '1 1 100%', mb: 1, p: 1, borderRadius: 2,
              bgcolor: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.2)',
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <AttachFileIcon fontSize="small" sx={{ color: '#2196F3' }} />
              <Typography variant="caption" color="text.secondary">
                No SMTP configured. PDF will auto-download — attach it to the email that opens.
                &nbsp;<strong>Go to Settings → SMTP Config</strong> to enable direct sending.
              </Typography>
            </Box>
          )}
          <Button onClick={() => setReminderOpen(false)} disabled={sendingReminder}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={sendingReminder ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            onClick={sendReminder}
            disabled={sendingReminder || generatingPdf || !reminderEmail}
            sx={{ borderRadius: 2 }}
          >
            {sendingReminder ? 'Sending...' : (smtpSettings?.host ? 'Send with Attachment' : 'Send Reminder + Download PDF')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden off-screen invoice template — rendered at page root so document.getElementById works */}
      {reminderInvoice && (
        <Box
          id="receivables-invoice-preview"
          sx={{
            position: 'fixed',
            left: -9999,
            top: 0,
            width: '794px', // standard A4 pixel width to prevent massive canvas / hanging issues
            pointerEvents: 'none',
            zIndex: -1,
            backgroundColor: '#fff',
          }}
        >
          <ZohoAkMoA4Template
            businessDetails={{
              name: businessSettings?.businessName || businessSettings?.name || 'My Business',
              address: businessSettings?.address || '',
              city: businessSettings?.city || '',
              state: businessSettings?.state || '',
              pincode: businessSettings?.pincode || '',
              gstin: businessSettings?.gstin || '',
              phone: businessSettings?.phone || '',
              email: businessSettings?.email || '',
              bankName: businessSettings?.bankName || '',
              bankBranch: businessSettings?.bankBranch || '',
              accountNumber: businessSettings?.accountNumber || '',
              ifsc: businessSettings?.ifsc || '',
              upiId: businessSettings?.upiId || '',
              gstRegistered: businessSettings?.gstRegistered || 'unregistered',
              gstType: businessSettings?.gstType || 'regular',
            }}
            clientDetails={{
              name: reminderInvoice.client,
              address: reminderInvoice.clientDetails?.address || '',
              city: reminderInvoice.clientDetails?.city || '',
              state: reminderInvoice.clientDetails?.state || '',
              pincode: reminderInvoice.clientDetails?.pincode || '',
              gstin: reminderInvoice.clientDetails?.gstin || '',
              phone: reminderInvoice.clientDetails?.phone || '',
              email: reminderInvoice.clientEmail || '',
            }}
            invoiceDetails={{
              invoiceNumber: reminderInvoice.invoiceNumber,
              invoiceDate: reminderInvoice.date,
              dueDate: reminderInvoice.dueDate,
            }}
            items={(reminderInvoice.items || []).map((item: any) => ({
              productName: item.name || item.productName || 'Item',
              description: item.description || '',
              quantity: Number(item.quantity) || 1,
              hsn: item.hsnCode || item.hsn || '',
              gstRate: Number(item.gstRate) || 0,
              taxableValue: Number(item.rate || item.taxableValue) * (Number(item.quantity) || 1),
              taxAmount: Number(item.taxAmount) || 0,
              total: Number(item.amount || item.total) || 0,
            }))}
            primaryColor={businessSettings?.primaryColor || '#3949AB'}
          />
        </Box>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Receivables;
